/**
 * useCoachVoice — thin hook over the browser's built-in
 * `window.speechSynthesis` API. Centralises voice enumeration, speak
 * + cancel, and mute behavior so the Coach page + Picker share a
 * single source of truth.
 *
 * Zero plugins. Works in every modern Chromium/Firefox/Safari.
 * Voice quality varies by OS — we never enrich beyond what the
 * platform exposes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

function getSynth() {
  if (typeof window === 'undefined') return null;
  return window.speechSynthesis || null;
}

export default function useCoachVoice() {
  const [voices, setVoices] = useState([]);
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);

  useEffect(() => {
    const synth = getSynth();
    if (!synth) return undefined;
    const load = () => setVoices(synth.getVoices() || []);
    load();
    // Some browsers populate voices asynchronously.
    synth.addEventListener?.('voiceschanged', load);
    return () => synth.removeEventListener?.('voiceschanged', load);
  }, []);

  const cancel = useCallback(() => {
    const synth = getSynth();
    if (!synth) return;
    synth.cancel();
    setSpeaking(false);
    utterRef.current = null;
  }, []);

  const speak = useCallback((text, { voiceUri, rate = 1.0, muted = false } = {}) => {
    const synth = getSynth();
    if (!synth || !text || muted) return;
    // Cancel any in-flight utterance before starting a new one — keeps
    // back-to-back chat replies from queueing up indefinitely.
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(String(text));
    if (voiceUri) {
      const match = (synth.getVoices() || []).find((v) => v.voiceURI === voiceUri);
      if (match) utter.voice = match;
    }
    utter.rate = Math.max(0.5, Math.min(2, Number(rate) || 1.0));
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => { setSpeaking(false); utterRef.current = null; };
    utter.onerror = () => { setSpeaking(false); utterRef.current = null; };
    utterRef.current = utter;
    synth.speak(utter);
  }, []);

  return {
    supported: !!getSynth(),
    voices,
    speak,
    cancel,
    speaking,
  };
}

/**
 * Pick a "best fit" voice URI given a persona hint. Used as the
 * default suggestion before the user explicitly picks one.
 * Prefers en-US, then matches gender (when the platform exposes
 * gendered voice names), then falls back to the first available.
 */
export function suggestVoiceUri(voices, voiceHint) {
  if (!voices || voices.length === 0) return null;
  const want = voiceHint?.gender || 'neutral';
  const lang = voiceHint?.lang || 'en-US';
  const enUs = voices.filter((v) => v.lang?.startsWith(lang)) || [];
  const pool = enUs.length ? enUs : voices;

  const isFemale = (v) => /female|samantha|victoria|karen|tessa|moira|fiona|veena|google us english female|zira/i.test(v.name);
  const isMale = (v) => /male|alex|daniel|fred|tom|google us english male|david/i.test(v.name);

  if (want === 'female') {
    return (pool.find(isFemale) || pool[0]).voiceURI;
  }
  if (want === 'male') {
    return (pool.find(isMale) || pool[0]).voiceURI;
  }
  // Neutral — just pick the first en-US voice the platform exposes.
  return pool[0].voiceURI;
}
